import {
    alpha,
    Avatar,
    Box, Button, Checkbox,
    Chip,
    createSvgIcon,
    Dialog, DialogContent, DialogTitle, Divider, Fab, Grid, IconButton, LinearProgress, Link, Paper, Skeleton, Stack, SxProps, Table, TableBody, TableCell, TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Theme,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import dayjs from 'dayjs'; // Import dayjs for date formatting
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { filtersToQueryParams, formatAddressObject, formatCurrency, IsSmallScreen, toTitleCase, toUpperCase } from '../../commonFunctions/CommonFunctions';
import http from '../../commonFunctions/httpClient';
import { WSIcons } from '../../commonFunctions/iIcons';

import config from '../../Config/config.json';
import { resetDataComponent, storeDataComponent } from '../../StateManagement/actions';
import store from '../../StateManagement/store';
import { FilterSearchText } from '../Filter/FilterSeacrchText';
import { WSFilterComponent } from '../Filter/WSFilterComponent';
import { useSnackbar } from '../Snackbar/SnackBarContext';
import { WSStatus } from '../Status/WSStatus';
import ThreeDotMenu from '../ThreeDotMenu/ThreeDotMenu';
import { WSView } from '../View/WSView';

interface DataGridProps {
    id: string;
    title?: string;
    subTitle?: string;
    columns: IColumns[];
    data?: Array<any>
    multiSelect?: boolean;
    headerType?: "simple" | "HaderTopCornered" | "modern"
    onSelectionChange?: (selectedRows: any[]) => void;
    source?: {
        url: string;
        pageSize: number;
        page: number;
        filter: any[];
        sort: any[];
        type?: 'POST' | 'GET'
    };
    filters?: any;
    filterFields?: any
    exportCsv?: boolean,
    importCsv?: boolean,
    importCsvEndpoint?: string;
    addButton?: any;
    transFormData?: (data: any, totalCount?: any) => any;
    onAddButtonClick?: () => void;
    buttonStyle?: 'Normal' | 'Fab',
    showpagination?: boolean;
    gridHeaderSx?: SxProps<Theme>;
    handleCheckBoxChangedValues?: (data: Array<any>) => void
    searchBox?: { fieldName?: string, searchMode?: 'field' | 'query' }
    isFilterChanged?: boolean
    rowsPerPageCount?: number;

    clientSideSorting?: boolean;
    clientSideSortingField?: string;

}
export interface IColumns {
    header: string;
    fieldName: string;
    type?: 'default' | 'link' | { image: { suffix1?: string; suffix2?: string; } } | 'action' | 'status' | 'date' | 'color' | 'img' | 'checkbox' | 'dateTime' | 'time' | 'amount' | 'None';
    tooltip?: boolean;
    actions?: any;
    defaultSort?: string;
    isHide?: boolean,
    isTitleCase?: boolean;
    actionCallback?: (action: string, row: any) => void;
    sortable?: boolean; // Indicate if column is sortable
    sortKey?: string
    showInLineAction?: {
        fieldName: string, valueToCheck: [{ fieldValue: any }], onClick?: (e: any, row: any) => void; actionString?: string
    },
    fieldCompare?: { fieldName: string, ValueCheck: { value: any, actionCheck: any } }
    hideAction?: {
        actionName: {
            fieldName: string,
            values: string[],
            hideAction: any[]
        }[]

    },
    hideSelectedActions?: HideSelectedActions
}
export interface HideSelectedActions {
    fieldToHide: {
        field: string;
        hideValue: string[];
        hideActions: string[];
    }[];
}
const WSDataGrid: React.FC<DataGridProps> = ({
    id,
    columns,
    multiSelect,
    onSelectionChange,
    source,
    filters,
    filterFields,
    exportCsv = false,
    importCsv = false,
    transFormData,
    title,
    subTitle,
    addButton,
    onAddButtonClick,
    buttonStyle = "Normal",
    showpagination = true,
    gridHeaderSx,
    data,
    handleCheckBoxChangedValues,
    searchBox,
    headerType = "simple",
    isFilterChanged = false,
    rowsPerPageCount = 5,
    clientSideSorting = false,
    clientSideSortingField,
    importCsvEndpoint
}) => {
    const [page, setPage] = useState(0);
    const [_rows, setRows] = useState<any[]>(data || []);
    const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageCount);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [selectedRowsForColumn, setSelectedRowsForColumn] = useState<number[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [exporting, setExporting] = useState<boolean>(false);
    const [showImagePopup, setShowImagePopup] = useState<boolean>(false);
    const [popupImageURL, setPopupImageURL] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const [gridHeight, setGridHeight] = useState<any>()
    const [newFilters, setFilters] = useState(filters);
    const [search, setSearch] = useState<string>('');

    const { showMessage } = useSnackbar();
    const theme = useTheme();

    const isMobile = IsSmallScreen()
    const ExportIcon = createSvgIcon(
        <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />,
        'SaveAlt',
    );
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
        key: '',
        direction: 'desc',
    });
    const labelFontSize = '0.75rem'
    const dispatch = useDispatch();
    const handleImageClick = (imageUrl: string) => {
        setPopupImageURL(imageUrl);
        setShowImagePopup(true);
    };

    const fetchDataFromAPI = async (
        page: number,
        pageSize: number,
        filter: any[] = [],
        sort: any[] = [],
        searchValue: string = ''
    ) => {
        try {
            setLoading(true);

            if (source?.type === "GET") {
                // Convert filters to query string
                const queryString = filtersToQueryParams(filter, searchValue, page, pageSize);

                const response: any = await http.get(`${source.url}?${queryString}`);
                return response;
            }

            if (source) {
                const response: any = await http.post(source.url, {
                    page,
                    pageSize,
                    filter,
                    sort: sort?.length > 0 ? sort : source?.sort?.length > 0 ? source.sort : sort,
                });

                return response;
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadPageData = useCallback(async () => {
        try {

            const defaultSortKey = columns?.find((column: any) => column?.defaultSort);

            const sortKey: any = sortConfig.key
                ? sortConfig.key
                : defaultSortKey
                    ? defaultSortKey.defaultSort
                    : '';

            const sort = [{ field: toTitleCase(sortKey), direction: sortConfig.direction }];

            let filter: any = [];

            if (newFilters && newFilters.length > 0) {
                filter = [...newFilters];
            } else if (source?.filter && source.filter.length > 0) {
                filter = [...source.filter];
            }

            console.log('Fetching data with parameters:', {
                page: page + 1,
                pageSize: rowsPerPage,
                filter: filter,
                sort: sort
            });

            let response = await fetchDataFromAPI(
                page + 1,
                rowsPerPage,
                filter && filter?.length > 0 ? filter : newFilters,
                sortKey !== '' ? sort : [],
                search
            );

            dispatch(storeDataComponent(id, response?.data?.results || []));

            setTotalCount(response?.data?.totalCount | 0);

            let gridData = response?.data?.results || [];

            // Client side sorting
            if (clientSideSorting && clientSideSortingField) {

                gridData = [...gridData].sort((a: any, b: any) => {

                    const aValue = a[clientSideSortingField];
                    const bValue = b[clientSideSortingField];

                    if (!aValue || !bValue) return 0;

                    // Date sorting
                    if (!isNaN(Date.parse(aValue))) {
                        return new Date(bValue).getTime() - new Date(aValue).getTime();
                    }

                    // Number sorting
                    if (!isNaN(aValue) && !isNaN(bValue)) {
                        return bValue - aValue;
                    }

                    // String sorting
                    return String(bValue).localeCompare(String(aValue));
                });
            }

            if (transFormData) {
                setRows(transFormData(gridData, response?.data?.totalCount));
            } else {
                setRows(gridData);
            }

        } catch (error) {
            console.error('Error loading page data:', error);
        }

    }, [page, rowsPerPage, search, source, id, sortConfig, newFilters, isFilterChanged, transFormData, clientSideSorting, clientSideSortingField]);


    useEffect(() => {

        if (data && data?.length > 0) {

            const startIndex: any = page !== 0 ? (page) * rowsPerPage : page;
            const paginatedData: any = data.slice(startIndex, startIndex + rowsPerPage);

            setRows(paginatedData);
            //setRows(data);
            setTotalCount(data?.length)

            //  setRowsPerPage(5)
            dispatch(storeDataComponent(id, data || []));
        } else {
            loadPageData();
        }

        if (gridContainerRef.current) {
            setGridHeight(gridContainerRef.current.clientHeight);

        }
    }, [page, rowsPerPage, sortConfig, newFilters, isFilterChanged, data, search]);

    useEffect(() => {
        const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            if (state.components[id]?.data === null) {
                loadPageData();
            }
        });

        return () => {
            unsubscribe();
        };
    }, [id, loadPageData]);
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value));
        setPage(0); // Reset page to 0 when rows per page changes
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelectedRows = _rows.map((_, index) => page * rowsPerPage + index);
            setSelectedRows(newSelectedRows);
            if (onSelectionChange) {
                onSelectionChange(newSelectedRows.map((idx) => _rows[idx % rowsPerPage]));
            }
            return;
        }
        setSelectedRows([]);
        if (onSelectionChange) {
            onSelectionChange([]);
        }
    };
    const handleSelectAllClickForColumns = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelectedRows = _rows.map((_, index) => page * rowsPerPage + index);
            setSelectedRowsForColumn(newSelectedRows);
            if (handleCheckBoxChangedValues) {
                handleCheckBoxChangedValues(newSelectedRows.map((idx) => _rows[idx % rowsPerPage]));
            }
            return;
        }
        setSelectedRowsForColumn([]);
        if (handleCheckBoxChangedValues) {
            handleCheckBoxChangedValues([]);
        }
    };

    const handleClick = (index: number) => {
        const selectedIndex = selectedRows.indexOf(index);

        let newSelectedRows: number[] = [];

        if (selectedIndex === -1) {
            newSelectedRows = newSelectedRows.concat(selectedRows, index);
        } else if (selectedIndex === 0) {
            newSelectedRows = newSelectedRows.concat(selectedRows.slice(1));
        } else if (selectedIndex === selectedRows?.length - 1) {
            newSelectedRows = newSelectedRows.concat(selectedRows.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelectedRows = newSelectedRows.concat(
                selectedRows.slice(0, selectedIndex),
                selectedRows.slice(selectedIndex + 1)
            );
        }
        setSelectedRows(newSelectedRows);

        if (onSelectionChange) {
            onSelectionChange(newSelectedRows.map((idx) => _rows[idx % rowsPerPage]));
        }

    };
    const handleClickForColumns = (index: number) => {

        const selectedIndexForColumn = selectedRowsForColumn.indexOf(index);

        let newSelectedRowsForColumn: number[] = [];

        if (selectedIndexForColumn === -1) {
            newSelectedRowsForColumn = newSelectedRowsForColumn.concat(selectedRowsForColumn, index);
        } else if (selectedIndexForColumn === 0) {
            newSelectedRowsForColumn = newSelectedRowsForColumn.concat(selectedRowsForColumn.slice(1));
        } else if (selectedIndexForColumn === selectedRowsForColumn?.length - 1) {
            newSelectedRowsForColumn = newSelectedRowsForColumn.concat(selectedRowsForColumn.slice(0, -1));
        } else if (selectedIndexForColumn > 0) {
            newSelectedRowsForColumn = newSelectedRowsForColumn.concat(
                selectedRowsForColumn.slice(0, selectedIndexForColumn),
                selectedRowsForColumn.slice(selectedIndexForColumn + 1)
            );
        }
        setSelectedRowsForColumn(newSelectedRowsForColumn)

        if (handleCheckBoxChangedValues) {
            handleCheckBoxChangedValues(newSelectedRowsForColumn.map((idx) => _rows[idx % rowsPerPage]));
        }
    };
    const isSelected = (index: number) => selectedRows.indexOf(index) !== -1;
    const isSelectedForColumn = (index: number) => selectedRowsForColumn.indexOf(index) !== -1;
    const handleRequestSort = (property: string) => {
        const isAsc = sortConfig.key?.toLowerCase() === property?.toLowerCase() && sortConfig.direction === 'asc';
        setSortConfig({ key: property, direction: isAsc ? 'desc' : 'asc' });
        setPage(0);
    };

    const sortedRows = () => {
        if (sortConfig.key) {
            const comparator = (a: any, b: any) => {
                if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
                if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            };
            return [..._rows].sort(comparator);
        }
        return _rows;
    };

    const isArrayOrObject = (value: any) => {
        return value && typeof value === 'object';
    };

    const handleFilterChange = (newFilters: any) => {
        console.log("New Filters from WSFilterComponent:", newFilters);

        if (source?.filter && source.filter.length > 0) {
            newFilters.push(...source.filter); // spread each item from source.filter into newFilters
        }

        setFilters(newFilters);
    };

    const handleAddButton = () => {
        if (onAddButtonClick) {
            onAddButtonClick();
        }
    }
    const formatExportData = (rows: any[], columns: IColumns[]) => {
        return rows.map((row) => {
            const formattedRow: any = {};
            columns?.forEach((column) => {
                if (column.fieldName.includes('addresses')) {
                    const formattedAddress: any = formatAddressObject(row[column.fieldName]);
                    formattedRow[column.fieldName] = `${formattedAddress.addressLine1 || ''} ${formattedAddress.addressLine2 || ''} ${formattedAddress.city || ''} ${formattedAddress.state || ''} ${formattedAddress.zipCode || ''}`;
                } else {
                    formattedRow[column.fieldName] = row[column.fieldName];
                }
            });
            return formattedRow;
        });
    };
    function convertTo12HourFormat(time24: string): string {
        const [hour, minute] = time24.split(":").map(Number);
        const period = hour >= 12 ? "PM" : "AM";
        const hour12 = hour % 12 === 0 ? 12 : hour % 12;
        return `${hour12}:${minute.toString().padStart(2, "0")} ${period}`;
    }

    const handleExportCSV = async () => {
        setExporting(true);
        const exportData = formatExportData(_rows, columns);
        const csvContent = [
            columns?.map((column) => column.header).join(','),
            ...exportData.map((row) =>
                columns?.map((column) => `"${row[column.fieldName] || ''}"`).join(',')
            ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${id}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setExporting(false);
    };


    const handleImportCSV = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = async (e) => {
            const text = e.target?.result as string;

            const rows = text.split('\n').map(row => row.split(','));
            const headers = rows[0];

            const data = rows.slice(1).map((row) => {
                const obj: any = {};
                headers.forEach((header, index) => {
                    obj[header.trim()] = row[index]?.replace(/"/g, '').trim();
                });
                return obj;
            });

            // If endpoint is provided, send CSV to server
            if (importCsvEndpoint) {
                try {
                    const formData = new FormData();
                    formData.append("file", file);

                    await http.postFormData(importCsvEndpoint, formData);
                    showMessage("Excel uploaded successfully", "success");
                    resetDataComponent(id);
                } catch (err) {
                    console.error("Error uploading Excel:", err);
                    showMessage("Failed to import Excel", "error");
                }
            } else {
                // fallback: just store locally
                setRows(data);
            }
        };

        reader.readAsText(file);
    };


    const handleSearchBoxFilterChange = (
        updatedFilters: any[],
        searchValue?: string
    ) => {
        console.log("updated filters", updatedFilters)
        setFilters(updatedFilters);

        console.log("searchValue", searchValue);
        setSearch(searchValue || "");
        setPage(0);
    };

    return (
        <div className="data-grid-container" ref={gridContainerRef} style={{ width: '100%' }}>
            <Grid item container spacing={1} justifyContent={'start'}  >
                <Grid item sm={12} xs={12} style={{ width: '100%' }}>
                    <Paper style={{ width: '100%' }}>

                        {headerType === "simple" && (
                            <Grid
                                item
                                container
                                spacing={1}
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{
                                    marginTop: 0,
                                    marginLeft: 0,
                                    width: '100%',
                                    borderTopLeftRadius: 8,
                                    borderTopRightRadius: 8,
                                    ...gridHeaderSx,
                                    backgroundColor: theme.palette.secondary.main,
                                    minHeight: 64,
                                    px: 2,
                                    py: 1,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                    borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                                }}
                            >
                                { }
                                <Grid
                                    item
                                    xs={12}
                                    md={5}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    {title && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    id="tableTitle"
                                                    sx={{
                                                        fontSize: '1.1rem',
                                                        fontWeight: 600,
                                                        letterSpacing: '0.3px',
                                                        color: '#fff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    {title}

                                                    <Chip
                                                        label={`${totalCount} Records`}
                                                        size="small"
                                                        sx={{
                                                            ml: 1,
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                                            color: '#fff',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500,
                                                            height: 24,
                                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                                        }}
                                                    />
                                                </Typography>
                                            </Box>

                                            {subTitle && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: alpha('#fff', 0.85),
                                                        fontSize: "0.8rem",
                                                        mt: 0.8,
                                                        fontWeight: 400
                                                    }}
                                                >
                                                    {subTitle}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Grid>

                                { }
                                <Grid item xs={12} md={7}>
                                    <Stack
                                        direction="row"
                                        spacing={5}
                                        justifyContent="flex-end"
                                        alignItems="center"
                                        flexWrap="wrap"
                                    >

                                        { }
                                        {searchBox && (
                                            <FilterSearchText
                                                rows={{ fieldName: searchBox.fieldName }}
                                                onFilterChange={handleSearchBoxFilterChange}
                                                defaultFilter={source?.filter}
                                            />
                                        )}

                                        { }
                                        {addButton && buttonStyle === "Normal" && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={addButton?.showIcon !== false && <WSIcons.AddIcon />}
                                                onClick={handleAddButton}
                                                sx={{
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: "#fff",
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    borderRadius: 2,
                                                    height: 38,
                                                    px: 2.5,
                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                                    "&:hover": {
                                                        backgroundColor: theme.palette.primary.dark,
                                                        transform: "translateY(-1px)",
                                                    },
                                                }}
                                            >
                                                {addButton?.label || "Add New"}
                                            </Button>
                                        )}

                                        { }
                                        {exportCsv && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<ExportIcon />}
                                                onClick={handleExportCSV}
                                                disabled={exporting}
                                                sx={{
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: "#fff",
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    borderRadius: 2,
                                                    height: 38,
                                                    px: 2.5,
                                                    "&:hover": {
                                                        backgroundColor: theme.palette.success.dark,
                                                    },
                                                }}
                                            >
                                                {exporting ? "Exporting..." : "Export"}
                                            </Button>
                                        )}


                                        {importCsv && (
                                            <>
                                                <input
                                                    type="file"
                                                    accept=".csv, .xlsx"
                                                    id="csv-upload"
                                                    style={{ display: "none" }}
                                                    onChange={handleImportCSV}
                                                />

                                                <label htmlFor="csv-upload">
                                                    <IconButton
                                                        component="span"
                                                        sx={{
                                                            backgroundColor: "#2b2f36",
                                                            color: "#fff",
                                                            width: 36,
                                                            height: 36,
                                                            "&:hover": {
                                                                backgroundColor: "#3b4048"
                                                            }
                                                        }}
                                                    >
                                                        <WSIcons.ImportIcon />
                                                    </IconButton>
                                                </label>
                                            </>
                                        )}

                                        { }
                                        {filterFields && (
                                            <WSFilterComponent
                                                headerType={headerType}
                                                rows={filterFields}
                                                alignment="right"
                                                onFilterChange={handleFilterChange}
                                            />
                                        )}

                                    </Stack>
                                </Grid>
                            </Grid>
                        )}

                        {headerType === "modern" && (
                            <Box
                                sx={{
                                    px: 2,
                                    pt: 2,
                                    pb: 1,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                }}
                            >
                                { }
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: "1.85rem"
                                    }}
                                >
                                    {title}
                                </Typography>

                                { }
                                {searchBox && (
                                    <FilterSearchText
                                        rows={{ fieldName: searchBox.fieldName }}
                                        onFilterChange={handleSearchBoxFilterChange}
                                        defaultFilter={source?.filter}
                                        searchMode={searchBox.searchMode}
                                    />
                                )}

                                { }
                                <Stack direction="row" spacing={1}>
                                    {addButton && (
                                        <Tooltip title="Add" arrow>
                                            <IconButton
                                                onClick={handleAddButton}
                                                sx={{
                                                    backgroundColor: "#2b2f36",
                                                    color: "#fff",
                                                    width: 36,
                                                    height: 36,
                                                    "&:hover": {
                                                        backgroundColor: "#3b4048"
                                                    }
                                                }}
                                            >
                                                <WSIcons.AddIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {exportCsv && (
                                        <Tooltip title="Export CSV" arrow>
                                            <IconButton
                                                onClick={handleExportCSV}
                                                disabled={exporting}
                                                sx={{
                                                    backgroundColor: "#2b2f36",
                                                    color: "#fff",
                                                    width: 36,
                                                    height: 36,
                                                    "&:hover": {
                                                        backgroundColor: "#3b4048"
                                                    }
                                                }}
                                            >
                                                <WSIcons.ExportIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {importCsv && (
                                        <>
                                            <input
                                                type="file"
                                                accept=".csv, .xlsx"
                                                id="csv-upload"
                                                style={{ display: "none" }}
                                                onChange={handleImportCSV}
                                            />

                                            <label htmlFor="csv-upload">
                                                <Tooltip title="Import CSV" arrow>
                                                    <IconButton
                                                        component="span"
                                                        sx={{
                                                            backgroundColor: "#2b2f36",
                                                            color: "#fff",
                                                            width: 36,
                                                            height: 36,
                                                            "&:hover": {
                                                                backgroundColor: "#3b4048"
                                                            }
                                                        }}
                                                    >
                                                        <WSIcons.ImportIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </label>
                                        </>
                                    )}

                                    {filterFields && (
                                        <WSFilterComponent
                                            headerType={headerType}
                                            rows={filterFields}
                                            alignment="right"
                                            dateType="datetime"
                                            onFilterChange={handleFilterChange}
                                        />
                                    )}
                                </Stack>
                            </Box>
                        )}

                        {headerType === "HaderTopCornered" && <Grid item container spacing={1} justifyContent={'start'} sx={{ marginTop: 0, marginLeft: 0, width: '100%', backgroundColor: "#499426", borderTopLeftRadius: 5, borderTopRightRadius: 5, ...gridHeaderSx }}>
                            {title && <Grid alignContent={'center'} style={{ paddingLeft: 20, paddingTop: 0, height: '4rem' }} item sm={4} className='ws-data-grid-lightviolet-title' ><Typography variant="h6" id="tableTitle" sx={{ fontSize: '1.15rem' }} >
                                {title}
                            </Typography>
                            </Grid>
                            }
                            {!title && <Grid style={{ paddingLeft: 20, paddingTop: 25, height: '4rem' }} item sm={4} className='ws-data-grid ws-data-grid-title' >
                            </Grid>
                            }
                            <Grid item sm={8} container spacing={2} justifyContent={'end'}>

                                {addButton && <Grid item style={{ paddingTop: 25 }}>

                                    {buttonStyle === "Normal" && <IconButton
                                        sx={{
                                            // backgroundColor: '#fff',
                                            //  opacity:0,
                                            color: '#fff',
                                            borderRadius: 1,
                                            //padding: '8px',
                                            width: '38px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginRight: (!exportCsv || !addButton) ? 1 : 0,
                                            justifyContent: 'center',
                                            '&:hover': {
                                                backgroundColor: '#706cae', // Change the color on hover
                                                //transform: 'scale(1.05)', // Optional: Slightly scale up the button on hover
                                            },

                                        }}
                                        onClick={handleAddButton}>
                                        <WSIcons.AddIcon style={{ color: 'white' }} />

                                    </IconButton>}
                                    {buttonStyle === "Fab" && <Fab size="small" onClick={handleAddButton} sx={{
                                        backgroundColor: '#404040',
                                        '&:hover': {
                                            backgroundColor: '#606060',
                                        },
                                        height: 32
                                    }} aria-label="add">
                                        <WSIcons.AddIcon style={{ color: 'white' }} />
                                    </Fab>}
                                </Grid>}
                                {exportCsv && <Grid item sx={{ paddingRight: !filterFields ? 2 : undefined }} style={{ paddingTop: 25 }}>
                                    {buttonStyle === 'Normal' && <Button sx={{
                                        backgroundColor: '#499426',
                                        height: '32px', fontSize: '0.75rem', '&:hover': {
                                            backgroundColor: '#706cae',
                                        },
                                    }}
                                        variant="contained"
                                        color="primary"
                                        startIcon={<ExportIcon />}
                                        onClick={handleExportCSV}
                                        disabled={exporting}
                                    >
                                        Export
                                    </Button>
                                    }
                                    {buttonStyle === 'Fab' &&
                                        <Fab sx={{ height: 32 }} variant="extended" size="medium" color="primary" onClick={handleExportCSV}>
                                            <ExportIcon sx={{ mr: 1 }} />
                                            Export
                                        </Fab>
                                    }
                                </Grid>
                                }

                                {
                                    searchBox && <Grid item sx={{ paddingRight: 2 }} style={{ paddingTop: 0 }}> <FilterSearchText rows={{ fieldName: searchBox.fieldName }} onFilterChange={handleSearchBoxFilterChange} defaultFilter={source?.filter} /></Grid>
                                }
                                {filterFields && <Grid item sx={{ paddingRight: 2 }} style={{ paddingTop: 25 }}>
                                    <WSFilterComponent headerType={headerType} rows={filterFields} alignment="right" onFilterChange={handleFilterChange}></WSFilterComponent>
                                </Grid>}
                            </Grid>
                        </Grid>}
                        <Divider style={{ marginTop: 0 }} />

                        <Table
                            size="small"
                            sx={{
                                "& .MuiTableCell-root": {
                                    borderBottom: "none",
                                    paddingTop: "12px",
                                    paddingBottom: "12px"
                                },
                                "& .MuiTableRow-root:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)"
                                }
                            }}
                        >
                            <TableHead
                                sx={{
                                    background: "linear-gradient(90deg,#2b2f36,#1c1f24)",
                                    boxShadow: "0px 10px 10px rgba(0,0,0,0.25)",

                                    "& th": {
                                        color: "#fff",
                                        fontWeight: 600,
                                        fontSize: "0.9rem",
                                        borderBottom: "none",
                                        paddingTop: "14px",
                                        paddingBottom: "14px",
                                        lineHeight: "70px"
                                    }
                                }}
                            >
                                <TableRow
                                    sx={{
                                        "& th:first-of-type": {
                                            borderTopLeftRadius: 12
                                        },
                                        "& th:last-of-type": {
                                            borderTopRightRadius: 12
                                        },
                                        "&:hover": {
                                            backgroundColor: "rgba(0,0,0,0.04)"
                                        }
                                    }}
                                >
                                    {multiSelect && (
                                        <TableCell padding="checkbox">
                                            {/* <Checkbox
                                                indeterminate={
                                                    selectedRows?.length > 0 && selectedRows?.length < rowsPerPage
                                                }
                                                checked={selectedRows?.length === rowsPerPage}
                                                onChange={handleSelectAllClick}
                                                inputProps={{ 'aria-label': 'select all rows' }}
                                            /> */}
                                        </TableCell>
                                    )}

                                    {columns?.map((column, index) => (
                                        !column.isHide && <TableCell className='ws-data-grid-header'
                                            key={index}
                                            sortDirection={sortConfig.key?.toLowerCase() === column.fieldName.toLowerCase() ? sortConfig.direction : false}
                                        >
                                            {column.type === 'checkbox' && (
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        indeterminate={
                                                            selectedRowsForColumn?.length > 0 && selectedRowsForColumn?.length < rowsPerPage
                                                        }
                                                        checked={selectedRowsForColumn?.length === rowsPerPage}
                                                        id={`chk-new-header${index}`}
                                                        name={`chk-new-header${index}`}
                                                        onChange={handleSelectAllClickForColumns}
                                                        inputProps={{ 'aria-label': 'select all rows' }}
                                                    />
                                                </TableCell>
                                            )}
                                            {(column.sortable && !column.isHide) ? (
                                                <TableSortLabel
                                                    active={sortConfig.key?.toLowerCase() === column.fieldName.toLowerCase()}
                                                    direction={sortConfig.key?.toLowerCase() === column.fieldName.toLowerCase() ? sortConfig.direction : 'asc'}
                                                    onClick={() => handleRequestSort(column.sortKey ? column.sortKey : column.fieldName)}
                                                >
                                                    {toUpperCase(column.header)}
                                                </TableSortLabel>
                                            ) : (
                                                <Typography sx={{ fontSize: labelFontSize, fontWeight: 'bold', color: "#ffff" }}>{toUpperCase(column.header)}</Typography>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            {sortedRows()?.length > 0 ? (<TableBody>
                                {loading ? (
                                    <>
                                        { }
                                        <TableRow component="th" >
                                            <TableCell
                                                colSpan={columns?.length + (multiSelect ? 1 : 0)}
                                                align="center"
                                            >
                                                <Box
                                                    display="flex"
                                                    width="100%"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    minHeight={gridHeight - 200} // Adjust the height based on your grid size
                                                >
                                                    <Grid
                                                        container
                                                        spacing={2}
                                                        width="90%"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                    >
                                                        <Grid item sm={12}>
                                                            { }
                                                            <Skeleton width="100%">
                                                                <Typography>.</Typography>
                                                            </Skeleton>

                                                            { }
                                                            <Skeleton
                                                                animation="pulse"
                                                                variant="rectangular"
                                                                width="100%"
                                                            >
                                                                <div style={{ paddingTop: '17%' }} />
                                                            </Skeleton>

                                                            <Skeleton width="100%">
                                                                <Typography>.</Typography>
                                                            </Skeleton>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )
                                    : (sortedRows().map((row: any, index: number) => {
                                        const isItemSelected = isSelected(page * rowsPerPage + index);
                                        const isItemSelectedForColumn = isSelectedForColumn(page * rowsPerPage + index)
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        const labelIdisItemSelectedForColumn = `enhanced-table-checkbox-isItemSelectedForColumn${index}`;
                                        return (
                                            <TableRow sx={{ overflow: 'auto' }}
                                                hover

                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={row.id}
                                                selected={isItemSelected}
                                            >
                                                {multiSelect && (
                                                    <TableCell padding="checkbox">
                                                        {/* <Checkbox id={`chk-sel${index}`}
                                                            onClick={() => handleClick(page * rowsPerPage + index)}
                                                            checked={isItemSelected}
                                                            inputProps={{ 'aria-labelledby': labelId }}
                                                            sx={{
                                                                color: "black",
                                                                '&.Mui-checked': {
                                                                    color: "black",
                                                                },
                                                                '&:hover': {
                                                                    backgroundColor: "rgba(0,0,0,0.04)",
                                                                },
                                                            }}
                                                        /> */}
                                                    </TableCell>
                                                )}
                                                {columns?.map((column, colIndex) => {

                                                    if (column.type === 'default' && !column?.isHide && column.tooltip === true) {
                                                        return (<TableCell key={colIndex}>
                                                            <WSView style={{ fontSize: '0.75rem' }} dotStartFrom={18}
                                                                title={column.isTitleCase ? toTitleCase(row[column.fieldName]) : row[column.fieldName]}
                                                                isTooltip={column.tooltip}
                                                            />
                                                        </TableCell>);
                                                    }

                                                    else if (column.type === "amount") {
                                                        return (
                                                            <TableCell key={colIndex}>
                                                                <Typography style={{ fontSize: "0.875rem", color: "#000" }}>
                                                                    {formatCurrency(row[column.fieldName])}
                                                                </Typography>
                                                            </TableCell>
                                                        );
                                                    }
                                                    else if (column.type === 'link' && !column?.isHide) {
                                                        return (
                                                            <TableCell key={colIndex} >
                                                                <Link sx={{ fontSize: labelFontSize }} href={row[column.fieldName]} target="_blank" rel="noopener noreferrer">
                                                                    {row[column.fieldName]}
                                                                </Link>
                                                            </TableCell>
                                                        );
                                                    } else if (typeof column.type === 'object' && column.type.image && !column?.isHide) {
                                                        return (
                                                            <TableCell key={colIndex}>
                                                                <Grid item container spacing={3}>
                                                                    <Grid item sm={4}>
                                                                        <img
                                                                            src={row[column.fieldName]}
                                                                            alt="thumbnail"
                                                                            onClick={() => handleImageClick(row[column.fieldName])}
                                                                            style={{ cursor: 'pointer', maxWidth: '100px', height: '40px' }}
                                                                        />
                                                                    </Grid>
                                                                    {column.type.image.suffix1 && <Grid item sm={4}>
                                                                        <Typography style={{ fontSize: '1rem', paddingTop: !column.type?.image?.suffix2 ? '10px' : '0' }}>{row[column.type.image.suffix1]}</Typography>
                                                                        {column.type?.image?.suffix2 && <Typography style={{ fontSize: '0.0.75rem' }}>{row[column.type?.image?.suffix2]}</Typography>}
                                                                    </Grid>}
                                                                </Grid>
                                                            </TableCell>
                                                        );
                                                    } else if (column.type === 'status' && !column?.isHide) {
                                                        return (
                                                            <TableCell key={colIndex}>
                                                                <WSStatus style={{ fontSize: labelFontSize }} value={row[column.fieldName]} />

                                                                {column.showInLineAction?.valueToCheck?.some((item: any) =>
                                                                    item.fieldValue?.toLowerCase?.() === row[column.fieldName]?.toLowerCase?.()
                                                                ) && (
                                                                        <Link
                                                                            component="button"
                                                                            underline="hover"

                                                                            sx={{ ml: 1, cursor: 'pointer', fontSize: '0.75rem' }} // Optional: adds spacing between WSStatus and link
                                                                            onClick={(e) => column.showInLineAction?.onClick?.(e, row)}
                                                                        >
                                                                            {column.showInLineAction?.actionString || 'Create'}
                                                                        </Link>
                                                                    )}
                                                            </TableCell>

                                                        );
                                                    }

                                                    else if (column.type === 'checkbox' && !column?.isHide) {
                                                        return (
                                                            <TableCell key={colIndex}>

                                                                <Checkbox id={`chkcolumn-${index}`} name={`chk-${index}`}
                                                                    checked={isItemSelectedForColumn}
                                                                    onClick={() => handleClickForColumns(page * rowsPerPage + index)}
                                                                    inputProps={{ 'aria-labelledby': labelIdisItemSelectedForColumn }}
                                                                />
                                                            </TableCell>
                                                        );
                                                    }
                                                    else if (column.type === 'img' && !column?.isHide) {
                                                        return (
                                                            <TableCell key={colIndex}>
                                                                <Grid item container spacing={3}>
                                                                    <Grid item sm={4}>
                                                                        <Tooltip title={<>  <img
                                                                            src={`${config.imgUrl}${row[column.fieldName]}`}
                                                                            alt="thumbnail"

                                                                            style={{ cursor: 'pointer', width: '200px', height: '150px' }}
                                                                        /></>}>
                                                                            <img
                                                                                src={`${config.imgUrl}${row[column.fieldName]}`}
                                                                                alt="thumbnail"
                                                                                onClick={() => window.open(`${config.imgUrl}${row[column.fieldName]}`, '_blank')}
                                                                                style={{ cursor: 'pointer', width: '50px', height: '40px' }}
                                                                            />
                                                                        </Tooltip>
                                                                    </Grid>
                                                                </Grid>
                                                            </TableCell>
                                                        );
                                                    }
                                                    else if (column.type === 'color' && !column?.isHide) {
                                                        return (
                                                            <TableCell key={colIndex}>
                                                                <Box display="flex" alignItems="center" >

                                                                    <Avatar
                                                                        sx={{ bgcolor: row[column.fieldName] ? row[column.fieldName] : 'gray', width: 24, height: 24, marginRight: 1, fontSize: '0.75rem', }}
                                                                        variant="rounded" defaultValue={''}
                                                                    ><></></Avatar>
                                                                    <Typography sx={{ fontSize: labelFontSize }} onDoubleClick={() => {
                                                                        const textToCopy = toTitleCase(row[column.fieldName] || '');
                                                                        navigator.clipboard.writeText(textToCopy).then(() => {
                                                                            showMessage(`Copied: ${textToCopy}`, 'success');
                                                                        });
                                                                    }}>{toTitleCase(row[column.fieldName])}</Typography>
                                                                </Box>
                                                            </TableCell>
                                                        );
                                                    }

                                                    else if (column.type === 'date' && !column?.isHide) {
                                                        return (
                                                            <TableCell key={colIndex}>
                                                                <Typography style={{ fontSize: labelFontSize }}>
                                                                    {dayjs(row[column.fieldName]).isValid() ? dayjs(row[column.fieldName]).format('DD/MM/YYYY') : 'Invalid Date'}
                                                                </Typography>
                                                            </TableCell>
                                                        );
                                                    }
                                                    else if (column.type === 'dateTime' && !column?.isHide) {
                                                        return (
                                                            <TableCell key={colIndex}>
                                                                <Typography style={{ fontSize: labelFontSize }}>
                                                                    {dayjs(row[column.fieldName]).isValid() ? dayjs(row[column.fieldName]).format('DD/MM/YYYY hh:mm') : 'Invalid Date'}
                                                                </Typography>
                                                            </TableCell>
                                                        );
                                                    }
                                                    else if (column.type === 'time' && !column?.isHide) {
                                                        return (
                                                            <TableCell key={colIndex}>
                                                                <Typography style={{ fontSize: labelFontSize }}>
                                                                    {convertTo12HourFormat(row[column.fieldName])}
                                                                </Typography>
                                                            </TableCell>
                                                        );
                                                    }
                                                    else if (column.type === 'action' && column?.actions?.length > 0) {
                                                        return (
                                                            <TableCell key={colIndex}>
                                                                <ThreeDotMenu
                                                                    align='vertical'
                                                                    field={column?.fieldCompare}
                                                                    items={column.actions}
                                                                    value={column?.fieldCompare ? row[column?.fieldCompare.fieldName] : undefined}
                                                                    rows={row}
                                                                    hideSelectedActions={column?.hideSelectedActions}
                                                                    onAction={(action: string) =>
                                                                        column.actionCallback?.(action, row)
                                                                    }
                                                                    hideAction={column.hideAction}
                                                                />
                                                            </TableCell>
                                                        );
                                                    }
                                                    else if (!column?.isHide) {
                                                        return (
                                                            <TableCell key={colIndex} >
                                                                <Typography sx={{ fontSize: labelFontSize }}>{isArrayOrObject(row[column.fieldName]) ? JSON.stringify(column.isTitleCase ? toTitleCase(row[column.fieldName]) : row[column.fieldName]) : column.isTitleCase ? toTitleCase(row[column.fieldName]) : row[column.fieldName]}</Typography>
                                                            </TableCell>
                                                        );
                                                    }
                                                })}
                                            </TableRow>
                                        );
                                    }))}
                            </TableBody>) : loading ? (
                                <>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell colSpan={columns?.length + (multiSelect ? 1 : 0)} align="center">
                                                <Box
                                                    display="block"
                                                    width="100%"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    minHeight={400}
                                                >
                                                    <LinearProgress />
                                                </Box>

                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                    { }

                                    { }

                                </>
                            ) : (
                                <TableBody>
                                    <TableRow>
                                        <TableCell colSpan={columns?.length + (multiSelect ? 1 : 0)} align="center">
                                            <Typography variant="h6" style={{ padding: '20px', fontSize: '1.15rem' }}>
                                                No Records to Show
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            )}
                        </Table>
                        { }
                        {showpagination === true && <TablePagination sx={{ fontSize: labelFontSize }}
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={totalCount}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />}
                    </Paper>

                </Grid>
            </Grid>
            <Dialog open={showImagePopup} onClose={() => setShowImagePopup(false)} maxWidth="xs">
                <DialogTitle>Image</DialogTitle>
                <DialogContent>
                    <img src={popupImageURL} alt="Large Preview" style={{ maxWidth: '100%' }} />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default WSDataGrid;
